import { useState } from 'react';
import { Search, ChevronRight } from 'lucide-react';
import './BuyerCriteriaWizard.css';

export function draftCriteriaStorageKey(slug) {
  return `a4a_draft_criteria_${slug}`;
}

const TIMELINE_OPTIONS = [
  { value: '', label: 'Not sure yet' },
  { value: 'ASAP', label: 'As soon as possible' },
  { value: '1-3 months', label: '1–3 months' },
  { value: '3-6 months', label: '3–6 months' },
  { value: '6+ months', label: '6+ months' },
];

const PROPERTY_TYPE_OPTIONS = [
  { value: '', label: 'No preference' },
  { value: 'single_family', label: 'Detached / single-family' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'condo', label: 'Condo / apartment' },
  { value: 'semi_detached', label: 'Semi-detached' },
];

const PRE_APPROVED_OPTIONS = [
  { value: '', label: 'Prefer not to say' },
  { value: 'yes', label: 'Yes, pre-approved' },
  { value: 'no', label: 'Not yet' },
  { value: 'not_yet', label: 'Working on it' },
];

export default function BuyerCriteriaWizard({
  agentName,
  aiName,
  initialDraft = {},
  onComplete,
  onSkip,
}) {
  const [draft, setDraft] = useState({
    price_min: initialDraft.price_min ?? '',
    price_max: initialDraft.price_max ?? '',
    bedrooms_min: initialDraft.bedrooms_min ?? '',
    bathrooms_min: initialDraft.bathrooms_min ?? '',
    areas: initialDraft.areas ?? '',
    timeline: initialDraft.timeline ?? '',
    pre_approved: initialDraft.pre_approved ?? '',
    property_type: initialDraft.property_types?.[0] ?? initialDraft.property_type ?? '',
    must_haves: initialDraft.must_haves_text ?? initialDraft.must_haves ?? '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDraft((d) => ({ ...d, [name]: value }));
  };

  const buildPayload = () => {
    const payload = {};
    if (draft.price_min) payload.price_min = Number(String(draft.price_min).replace(/,/g, ''));
    if (draft.price_max) payload.price_max = Number(String(draft.price_max).replace(/,/g, ''));
    if (draft.bedrooms_min) payload.bedrooms_min = parseInt(draft.bedrooms_min, 10);
    if (draft.bathrooms_min) payload.bathrooms_min = parseFloat(draft.bathrooms_min);
    if (draft.areas?.trim()) payload.areas = draft.areas.trim();
    if (draft.timeline) payload.timeline = draft.timeline;
    if (draft.pre_approved) payload.pre_approved = draft.pre_approved;
    if (draft.must_haves?.trim()) payload.must_haves_text = draft.must_haves.trim();
    return payload;
  };

  const hasAny = () => Object.keys(buildPayload()).length > 0;

  const handleSave = () => {
    const payload = buildPayload();
    onComplete(payload);
  };

  return (
    <div className="buyer-criteria-wizard">
      <div className="buyer-criteria-wizard__icon">
        <Search size={22} />
      </div>
      <h2 className="buyer-criteria-wizard__title">What are you looking for?</h2>
      <p className="buyer-criteria-wizard__desc">
        Optional — tell {aiName} your search basics now. If you create a saved search later,
        this carries over to your account and {agentName}&apos;s dashboard.
      </p>

      <div className="buyer-criteria-wizard__grid">
        <label className="buyer-criteria-wizard__field">
          <span>Min budget (CAD)</span>
          <input
            type="text"
            name="price_min"
            inputMode="numeric"
            placeholder="e.g. 200000"
            value={draft.price_min}
            onChange={handleChange}
          />
        </label>
        <label className="buyer-criteria-wizard__field">
          <span>Max budget (CAD)</span>
          <input
            type="text"
            name="price_max"
            inputMode="numeric"
            placeholder="e.g. 350000"
            value={draft.price_max}
            onChange={handleChange}
          />
        </label>
        <label className="buyer-criteria-wizard__field">
          <span>Bedrooms (min)</span>
          <input
            type="number"
            name="bedrooms_min"
            min="0"
            placeholder="e.g. 3"
            value={draft.bedrooms_min}
            onChange={handleChange}
          />
        </label>
        <label className="buyer-criteria-wizard__field">
          <span>Bathrooms (min)</span>
          <input
            type="number"
            name="bathrooms_min"
            min="0"
            step="0.5"
            placeholder="e.g. 2"
            value={draft.bathrooms_min}
            onChange={handleChange}
          />
        </label>
      </div>

      <label className="buyer-criteria-wizard__field buyer-criteria-wizard__field--full">
        <span>Areas (cities or neighbourhoods)</span>
        <input
          type="text"
          name="areas"
          placeholder="St. John's, Paradise, Mount Pearl, CBS…"
          value={draft.areas}
          onChange={handleChange}
        />
      </label>

      <label className="buyer-criteria-wizard__field buyer-criteria-wizard__field--full">
        <span>Property type</span>
        <select name="property_type" value={draft.property_type} onChange={handleChange}>
          {PROPERTY_TYPE_OPTIONS.map((o) => (
            <option key={o.value || 'none'} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>

      <label className="buyer-criteria-wizard__field buyer-criteria-wizard__field--full">
        <span>Timeline</span>
        <select name="timeline" value={draft.timeline} onChange={handleChange}>
          {TIMELINE_OPTIONS.map((o) => (
            <option key={o.value || 'none'} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>

      <label className="buyer-criteria-wizard__field buyer-criteria-wizard__field--full">
        <span>Pre-approved for a mortgage?</span>
        <select name="pre_approved" value={draft.pre_approved} onChange={handleChange}>
          {PRE_APPROVED_OPTIONS.map((o) => (
            <option key={o.value || 'none'} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>

      <label className="buyer-criteria-wizard__field buyer-criteria-wizard__field--full">
        <span>Must-haves & lifestyle</span>
        <textarea
          name="must_haves"
          rows={3}
          placeholder="e.g. close to schools, in-town, parking, back yard, rural vs city…"
          value={draft.must_haves}
          onChange={handleChange}
        />
      </label>

      <div className="buyer-criteria-wizard__actions">
        <button type="button" className="buyer-criteria-wizard__skip" onClick={onSkip}>
          Skip for now
        </button>
        <button
          type="button"
          className="buyer-criteria-wizard__save"
          onClick={handleSave}
        >
          {hasAny() ? 'Save & start chatting' : 'Start chatting'}
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
